// File: typescript/apps/front-end/src/main/common/GroupInviteCandidateTypes.ts
//
// Hand-written contract for the person-first group picker ("invite @someone to one of your groups").
// Rule 6: the read endpoints and the mutation's new result body are not in the generated client yet, so
// `ClientFactory` consumes them over the shared axios instance and the shapes live here.
//
// Design: designs/01-now/2026-08-19-invite-a-person-to-your-groups-design.md §5, §6.1, §6.2.

/**
 * What the viewer may do with this candidate group, decided by the SERVER and carried on the row.
 *
 * <p>Never re-derived in the client from `privateAccess && !viewerLeads`: the client does not hold the
 * viewer's leadership level per group, and a client that guesses it wrong either hides a capability or
 * renders a button that 403s. Design §5.</p>
 */
export type GroupInviteViewerAction = "INVITE" | "RECOMMEND";

/** The two tabs, and the `source` parameter the per-tab search endpoint takes. */
export type GroupInviteCandidateSource = "leader" | "member";

/**
 * One candidate row. A projection of the backend's `GroupsToInviteUserToDTO`, which extends `GroupDTO`
 * and therefore carries far more than the picker reads; only the fields the dialog renders are declared.
 */
export type GroupInviteCandidate = {
    publicId?: string;
    name?: string;
    description?: string | null;
    thumbnailUrl?: string | null;
    membersCount?: number | null;
    privateAccess?: boolean | null;
    sensitive?: boolean | null;
    creatorUsername?: string | null;
    creatorProfileImageUrl?: string | null;
    createdAt?: string | null;
    /** The invited person is already a member or a leader here. */
    isUserAlreadyInGroup?: boolean | null;
    /**
     * An invitation to this person is pending — OR a decline of theirs is still inside the cooldown.
     * One flag on purpose: a decline must not be distinguishable from a pending invitation (design §9.2).
     */
    isInvitePending?: boolean | null;
    /** They have already asked to join. With `viewerAction === "INVITE"` the invite verb approves that request. */
    hasPendingJoinRequest?: boolean | null;
    viewerAction?: GroupInviteViewerAction | string | null;
    /** Pinned false by the server until the recommendation table lands (design stage 3). */
    isRecommendationPending?: boolean | null;
};

/**
 * One round trip per open. Typing in the search field queries the per-tab candidate endpoint and must
 * never re-enter this one — the chooser specs assert exactly one bootstrap per open (design §5.2).
 *
 * <p>Each list is capped at the server's page size; the matching `*Truncated` flag says whether more
 * exist, so the dialog can point the member at its search box instead of letting the cap read as data
 * loss.</p>
 */
export type GroupInviteCandidatesBootstrapResponse = {
    leaderGroups?: GroupInviteCandidate[];
    memberGroups?: GroupInviteCandidate[];
    leaderGroupsTruncated?: boolean;
    memberGroupsTruncated?: boolean;
};

/** Mirrors `InviteUserToGroupResultDTO.Status`. */
export type InviteUserToGroupResultStatus =
    | "INVITED"
    | "ADDED"
    | "ALREADY_IN_GROUP"
    | "ALREADY_INVITED";

export type InviteUserToGroupResult = {
    status?: InviteUserToGroupResultStatus | string | null;
};

/**
 * Whether the picker may offer the member-side RECOMMEND action.
 *
 * <p><strong>True since 2026-08-20, when the recommendation verbs landed.</strong> It was false for the
 * few hours between the picker shipping and stage 3 shipping: the candidate read already labelled a
 * private group the viewer only belongs to as `RECOMMEND`, but the verb behind that button did not
 * exist, so the rows were dropped instead (the degrade design §15 names). Both verbs now exist on
 * `AjaxGroupRecommendationRestController` and `ClientFactory` wraps them.</p>
 *
 * <p>Kept as a named constant rather than deleted because
 * {@link filterOfferableGroupInviteCandidates} takes it as a parameter, which is what lets one test
 * assert the rows are offered and another assert the degrade still works. It is NOT a feature flag: it
 * has one value in every environment (rule 35), and the next reader who wants to ship something dark
 * should reach for a test instead.</p>
 */
export const GROUP_RECOMMENDATIONS_ENABLED = true;

/**
 * What a candidate row's right-hand action reads. Derived once, from the server's flags only, so the
 * dialog and its tests agree about precedence rather than each re-implementing it.
 */
export type GroupInviteRowState =
    /** Offer the invite. */
    | "invite"
    /** Offer the recommendation (private group the viewer only belongs to). */
    | "recommend"
    /** They asked to join and the viewer may approve it: inviting adds them immediately (design §7). */
    | "add"
    /** Inert: an invitation is pending, or a decline is inside the cooldown. */
    | "invited"
    /** Inert: a recommendation for this pair is unresolved or inside its cooldown. */
    | "recommended"
    /** Inert: already a member or a leader. */
    | "already_member"
    /** Inert: they are in the leaders' queue and this viewer cannot approve it. */
    | "already_asked";

/** The states that render as a muted label. */
export type InertGroupInviteRowState = Extract<
    GroupInviteRowState,
    "invited" | "recommended" | "already_member" | "already_asked"
>;

/** The states that render a pressable control. */
export type ActionableGroupInviteRowState = Exclude<GroupInviteRowState, InertGroupInviteRowState>;

/**
 * True for the states that render as a muted label rather than a pressable control.
 *
 * <p>A type predicate rather than a plain boolean, so the caller's label lookup narrows to the inert
 * half and the actionable half exhaustively. That is what makes a state added later a COMPILE error at
 * both label maps instead of a row that silently renders nothing.</p>
 */
export function isInertGroupInviteRowState(state: GroupInviteRowState): state is InertGroupInviteRowState {
    return state === "invited"
        || state === "recommended"
        || state === "already_member"
        || state === "already_asked";
}

/**
 * Precedence is deliberate and the order is the point: membership outranks every pending state, and a
 * pending invitation outranks the join-request crossover, because the invite verb would answer
 * `ALREADY_INVITED` for it anyway.
 */
export function deriveGroupInviteRowState(candidate: GroupInviteCandidate): GroupInviteRowState {
    if (candidate.isUserAlreadyInGroup === true) return "already_member";
    if (candidate.isInvitePending === true) return "invited";
    if (candidate.isRecommendationPending === true) return "recommended";

    const mayPreAuthorize = candidate.viewerAction !== "RECOMMEND";

    if (candidate.hasPendingJoinRequest === true) {
        return mayPreAuthorize ? "add" : "already_asked";
    }
    return mayPreAuthorize ? "invite" : "recommend";
}

/**
 * Drops the rows the picker cannot honour yet. See `GROUP_RECOMMENDATIONS_ENABLED`.
 *
 * <p>Takes the flag as a parameter rather than reading the module constant, so a test can prove BOTH
 * halves: that the rows are hidden today, and that they survive the moment the verbs exist.</p>
 */
export function filterOfferableGroupInviteCandidates(
    candidates: GroupInviteCandidate[] | null | undefined,
    recommendationsEnabled: boolean = GROUP_RECOMMENDATIONS_ENABLED,
): GroupInviteCandidate[] {
    if (!candidates) return [];
    if (recommendationsEnabled) return candidates;

    return candidates.filter((candidate) => {
        const state = deriveGroupInviteRowState(candidate);
        return state !== "recommend" && state !== "recommended" && state !== "already_asked";
    });
}
